import React, { useEffect, useState } from 'react';

import { InputAdornment, TextField } from '@mui/material';

type HourFieldProps = {
  /** 現在の値（数値文字列、空文字=未入力） */
  value: string;
  /** 値変更ハンドラ */
  onChange: (value: string) => void;
  /** ラベル */
  label?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** 必須表示 */
  required?: boolean;
};

/**
 * 時間入力コンポーネント
 *
 * - 数値のみ入力可能
 * - 入力状態でない場合は「時間」を単位として表示する
 */
const HourField: React.FC<HourFieldProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value);
    }
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    setLocalValue(value);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const cleaned = localValue.replace(/[^0-9.]/g, '');
    if (cleaned === '') {
      onChange('');
    } else {
      const num = parseFloat(cleaned);
      onChange(isNaN(num) ? '' : String(num));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // 数値と小数点のみ許可
    if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
      setLocalValue(raw);
    }
  };

  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      disabled={disabled}
      value={isFocused ? localValue : value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      slotProps={{
        input: {
          endAdornment:
            !isFocused && value ? <InputAdornment position="end">時間</InputAdornment> : undefined,
        },
      }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
    />
  );
};

export default HourField;
